import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Briefcase, Shield, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const PremiumWelcomeSection = () => {
  const { t, isRTL } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const cards = [
    {
      icon: FileText,
      title: t('welcome.card1.title'),
      description: t('welcome.card1.description'),
      gradient: 'from-purple-600 via-purple-500 to-blue-600',
      accentColor: 'purple'
    },
    {
      icon: Briefcase,
      title: t('welcome.card2.title'),
      description: t('welcome.card2.description'),
      gradient: 'from-orange-600 via-orange-500 to-amber-600',
      accentColor: 'orange'
    },
    {
      icon: Shield,
      title: t('welcome.card3.title'),
      description: t('welcome.card3.description'),
      gradient: 'from-emerald-600 via-emerald-500 to-teal-600',
      accentColor: 'emerald'
    }
  ];

  // Auto-play for mobile
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % cards.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, cards.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % cards.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + cards.length) % cards.length);
    setIsAutoPlaying(false);
  };

  return (
    <section className="relative py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
      {/* Premium Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="creative-grid absolute inset-0 opacity-5" />
        <div className="absolute top-1/4 -left-20 w-64 h-64 bg-gradient-to-r from-purple-200/10 to-orange-200/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-200/10 to-purple-200/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-12">
          <h2 className="text-2xl lg:text-3xl font-light text-slate-900 mb-3 lg:mb-4">
            {t('welcome.title')}
          </h2>
          <p className="text-base lg:text-lg text-slate-700 max-w-4xl mx-auto leading-relaxed font-light">
            {t('welcome.subtitle')}
          </p>
        </div>

        {/* Desktop: 3 Cards Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5 lg:gap-6 mb-10">
          {cards.map((card, index) => (
            <div key={index} className="group">
              <div className="premium-card relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-slate-200/50 overflow-hidden h-full">
                {/* Gradient Background Overlay - Only on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-3 transition-opacity duration-500`} />
                
                {/* Icon with Premium Gradient */}
                <div className="relative mb-4">
                  <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-500`}>
                    <card.icon className="w-7 h-7 text-white" />
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="relative text-lg font-semibold text-slate-900 mb-2.5 transition-all duration-500">
                  {card.title}
                </h3>
                
                {/* Description */}
                <p className="relative text-sm text-slate-600 leading-relaxed mb-4 group-hover:text-slate-700 transition-colors duration-500">
                  {card.description}
                </p>
                
                {/* Decorative Line with Gradient */}
                <div className="relative">
                  <div className={`h-0.5 w-full bg-gradient-to-r ${card.gradient} rounded-full opacity-30 group-hover:opacity-100 transition-all duration-500 transform origin-left scale-x-0 group-hover:scale-x-100`} />
                </div>

                {/* Premium Shine Effect */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: Carousel */}
        <div className="md:hidden relative mb-12">
          {/* Cards Container */}
          <div className="relative overflow-hidden rounded-3xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {cards.map((card, index) => (
                <div key={index} className="w-full flex-shrink-0 px-4">
                  <div className="premium-card relative bg-white rounded-3xl p-8 shadow-xl border border-slate-200/50 overflow-hidden">
                    {/* Gradient Background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-3`} />
                    
                    {/* Icon */}
                    <div className="relative mb-6 flex justify-center">
                      <div className={`w-20 h-20 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <card.icon className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="relative text-xl font-semibold text-slate-900 mb-3 text-center">
                      {card.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="relative text-sm text-slate-600 leading-relaxed mb-6 text-center">
                      {card.description}
                    </p>
                    
                    {/* Decorative Line */}
                    <div className="relative flex justify-center">
                      <div className={`h-1 w-24 bg-gradient-to-r ${card.gradient} rounded-full`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              className="p-2 rounded-full bg-white shadow-lg border border-slate-200 hover:scale-110 transition-transform duration-300"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            
            {/* Dots */}
            <div className="flex gap-2">
              {cards.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentSlide(index);
                    setIsAutoPlaying(false);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-8 bg-gradient-to-r from-purple-600 to-orange-500' 
                      : 'w-2 bg-slate-300'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={nextSlide}
              className="p-2 rounded-full bg-white shadow-lg border border-slate-200 hover:scale-110 transition-transform duration-300"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link 
            to="/kontakt"
            className="inline-flex items-center px-6 lg:px-8 py-3 lg:py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-sm lg:text-base transition-all duration-300 hover:shadow-glow hover:scale-105"
          >
            <span className={isRTL ? 'ml-2' : 'mr-2'}>{t('welcome.cta')}</span>
            <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          </Link>
        </div>
      </div>
    </section>
  );
};
